import {
  addSignalAttribute,
  identify,
  init,
  removeSignalAttribute
} from '@dash0/sdk-web';

import {
  APPLICATION_VERSION,
  DASH0_AUTH_TOKEN,
  DASH0_ENDPOINT,
  DASH0_ENVIRONMENT,
  DASH0_SAMPLING_RATE,
  DASH0_SERVICE
} from '~/common/helpers/environment';

type Dash0UserContext = {
  email: string;
  realm: string;
};

let initialized = false;

const USER_REALM_ATTRIBUTE = 'user.realm';
const RICH_SESSION_KEY = 'dash0.rich_session';
const ASSET_URLS = /\.(png|jpg|jpeg|gif|css|js|woff|woff2|ttf|svg|ico)$/i;

export function parseSessionSamplingRate(
  rate: number | string | undefined,
  isProd: boolean
): number {
  const parsed = typeof rate === 'number' ? rate : parseFloat(rate ?? '');

  if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 100) {
    return parsed;
  }

  return isProd ? 0 : 100;
}

// The SDK gates ALL signals — including reported errors — on one per-session
// sampling flag, with no per-signal override. So every session is sampled in
// (errors always transmit) and the sampling rate instead decides which sessions
// additionally run the high-volume RUM instrumentations. The decision is stored
// so it stays stable across reloads within a session.
function isRichSession(rate: number): boolean {
  try {
    const stored = sessionStorage.getItem(RICH_SESSION_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;

    const decided = (Math.random()*100) < rate;
    sessionStorage.setItem(RICH_SESSION_KEY, decided ? '1' : '0');
    return decided;
  } catch {
    return (Math.random()*100) < rate;
  }
}

export function setDash0UserContext(userData?: Dash0UserContext) {
  if (!userData) {
    identify();
    removeSignalAttribute(USER_REALM_ATTRIBUTE);
    return;
  }

  identify(userData.email, { email: userData.email });
  addSignalAttribute(USER_REALM_ATTRIBUTE, userData.realm);
}

export function initDash0() {
  if (initialized || !DASH0_AUTH_TOKEN) return;

  initialized = true;

  const rich = isRichSession(parseSessionSamplingRate(DASH0_SAMPLING_RATE, import.meta.env.PROD));

  init({
    serviceName: DASH0_SERVICE,
    serviceVersion: APPLICATION_VERSION || undefined,
    environment: DASH0_ENVIRONMENT,
    endpoint: {
      url: DASH0_ENDPOINT,
      authToken: DASH0_AUTH_TOKEN
    },
    enabledInstrumentations: rich ? undefined : ['@dash0/error'],
    ignoreUrls: [ASSET_URLS]
  });
}
